import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { Fragment } from 'react';
import { OutputRenderer } from './OutputRenderer';
import { orderedPlugins } from './pluginOrder';
import { daysInRange } from './rangeUtils';
import { DateRange, DayRun, PluginOutputs, RecalculateResponse, ShiftRun } from './types';

/** Plugin blocks in execution order with a divider between layers. */
const PluginBlocks = ({ outputs, stages }: { outputs: PluginOutputs; stages: string[][] }) => {
  const plugins = orderedPlugins(outputs, stages);
  return (
    <>
      {plugins.map((p, i) => (
        <Fragment key={p.pluginId}>
          {i > 0 && plugins[i - 1].layer !== p.layer && <Divider sx={{ my: 1 }}>↓ stage {p.layer + 1}</Divider>}
          <OutputRenderer pluginId={p.pluginId} outputs={outputs[p.pluginId]} />
        </Fragment>
      ))}
    </>
  );
};

const ShiftCard = ({ shift, stages }: { shift: ShiftRun; stages: string[][] }) => (
  <Card variant="outlined" sx={{ mb: 1 }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        shift {shift.shiftId}
      </Typography>
      {shift.error ? <Alert severity="error">{shift.error}</Alert> : <PluginBlocks outputs={shift.outputs ?? {}} stages={stages} />}
    </CardContent>
  </Card>
);

/** The day-grain plugins' raw outputs for one date, after its shifts: they ran once over the date, so they are one card, not one per shift. */
const DayCard = ({ day, stages }: { day: DayRun; stages: string[][] }) => (
  <Card variant="outlined" sx={{ mb: 1 }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        day-grain outputs
      </Typography>
      {day.error ? <Alert severity="error">{day.error}</Alert> : <PluginBlocks outputs={day.outputs ?? {}} stages={stages} />}
    </CardContent>
  </Card>
);

export const PipelineView = ({ response, range }: { response: RecalculateResponse; range: DateRange }) => {
  const { content, metadata } = response;
  const shiftsByDate = content.shifts.reduce<Record<string, ShiftRun[]>>((acc, s) => ({ ...acc, [s.date]: [...(acc[s.date] ?? []), s] }), {});
  const dayByDate = new Map((content.days ?? []).map((day) => [day.date, day]));
  const days = Array.from(new Set([...daysInRange(range), ...Object.keys(shiftsByDate), ...Array.from(dayByDate.keys())])).sort();
  const firstDayWithShifts = days.find((date) => (shiftsByDate[date] ?? []).length > 0);

  return (
    <Box>
      {days.map((date) => {
        const shifts = shiftsByDate[date] ?? [];
        const day = dayByDate.get(date);
        const hasError = shifts.some((s) => s.error) || Boolean(day?.error);
        return (
          <Accordion key={date} defaultExpanded={date === firstDayWithShifts || hasError} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontFamily: 'monospace', mr: 2 }}>{date}</Typography>
              <Typography color="text.secondary">{shifts.length === 0 ? 'no shifts' : `${shifts.length} shift${shifts.length > 1 ? 's' : ''}`}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {shifts.map((shift, i) => (
                <ShiftCard key={`${date}-${shift.shiftId}-${i}`} shift={shift} stages={metadata.stages} />
              ))}
              {day && <DayCard day={day} stages={metadata.stages} />}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
